import { useCallback, useMemo, useState } from "react"
import { Chart } from "react-chartjs-2"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    LineElement,
    PointElement,
} from 'chart.js'
import { faker } from "@faker-js/faker"
import { DateTime } from "luxon"
import * as d3 from "d3"
import { Card, FormControl, Grid, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material"
import { createEnumChecker, iterateStringEnum } from "../../../../lib/enums"
import { makeStyles } from "tss-react/mui"
import { UnreachableCaseError } from "../../../../lib/lang"
import { Box } from '@mui/system'

enum RollupPeriod {
    DAILY = "DAILY",
    WEEKLY = "WEEKLY",
    MONTHLY = "MONTHLY",
}

const isRollupPeriod = createEnumChecker(RollupPeriod)

type SurveyItem = Readonly<{
    date: DateTime
    ratings: number
    score: number
}>

function* datesFrom(from: DateTime, to: DateTime) {
    to = to.startOf("day")
    let cur = from.startOf("day")

    while (cur < to) {
        yield cur
        cur = cur.plus({ days: 1 })
    }
}

const dates = [...datesFrom(DateTime.fromISO("2022-01-01"), DateTime.fromISO("2022-05-01"))]
const rawData: SurveyItem[] = dates.map((date) => ({
    date,
    ratings: faker.number.int({ min: 0, max: 1000 }),
    score: faker.number.float({ min: 1, max: 5 })
}))

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
)

function getRollupPeriodLabel(period: RollupPeriod) {
    switch (period) {
        case RollupPeriod.DAILY:
            return "Daily"
        case RollupPeriod.WEEKLY:
            return "Weekly"
        case RollupPeriod.MONTHLY:
            return "Monthly"
        default:
            throw new UnreachableCaseError(period)
    }
}

const useStyles = makeStyles()((_theme) => ({

}))

type RollupGraphExampleProps = Readonly<{}>

export function RollupGraphExample({ }: RollupGraphExampleProps) {
    const { classes } = useStyles()

    const [rollupPeriod, setRollupPeriod] = useState(RollupPeriod.DAILY)

    const rolledUp = useMemo(() => {
        switch (rollupPeriod) {
            case RollupPeriod.DAILY:
                {
                    const data = rawData.map((d) => ([d.date.toISODate(), d]))
                    return data as [string, SurveyItem][]
                }
            case RollupPeriod.WEEKLY:
                {
                    const data = d3.rollups(
                        rawData,
                        (values) => ({
                            ratings: d3.sum(values, (v) => v.ratings),
                            score: d3.mean(values, (v) => v.score)
                        }),
                        (v) => `Week ${v.date.weekNumber}`
                    )
                    return data
                }
            case RollupPeriod.MONTHLY:
                {
                    const data = d3.rollups(
                        rawData,
                        (values) => ({
                            ratings: d3.sum(values, (v) => v.ratings),
                            score: d3.mean(values, (v) => v.score)
                        }),
                        (v) => `${v.date.monthLong}`
                    )
                    return data
                }
            default:
                throw new UnreachableCaseError(rollupPeriod)
        }
    }, [rawData, rollupPeriod])

    const handleChange = useCallback((e: SelectChangeEvent<RollupPeriod>) => {
        const value = e.target.value
        if (isRollupPeriod(value)) {
            setRollupPeriod(value)
        }
    }, [])

    return (
        <>

            <Grid container direction="column" wrap="nowrap" height="400px" >
                <Box sx={{ display: "flex" }}>
                    <Typography variant="h6" sx={{ marginLeft: '1rem' }}>
                        Satisfaction trend
                    </Typography>

                    <FormControl
                        size="small"
                        sx={{ marginLeft: "1rem" }}
                    >
                        <Select
                            id="period-select"
                            variant="outlined"
                            value={rollupPeriod}
                            onChange={handleChange}
                        >
                            {[...iterateStringEnum(RollupPeriod)].map((p) => (
                                <MenuItem key={p} value={p}>{getRollupPeriodLabel(p)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Chart
                    type="bar"

                    data={{
                        labels: rolledUp.map((b) => b[0]),
                        datasets: [
                            {
                                type: "bar" as const,
                                label: "Ratings",
                                data: rolledUp.map((b) => b[1].ratings),
                                backgroundColor: "rgba(255, 99, 132, 0.5)",
                                yAxisID: "y",
                            },
                            {
                                type: "line" as const,
                                label: "Score",
                                data: rolledUp.map((b) => b[1].score),
                                fill: false,
                                borderColor: "rgb(255, 99, 132)",
                                borderWidth: 2,
                                yAxisID: "y1",
                            }
                        ]
                    }}
                    options={{
                        responsive: true,
                        plugins: {
                            legend: {
                                position: 'top' as const,
                            },
                            title: {
                                display: true,
                                text: 'Ratings over time',
                            },
                        },
                        scales: {
                            y: {
                                type: "linear" as const,
                                display: true,
                                position: "left" as const,
                            },
                            y1: {
                                type: "linear" as const,
                                display: true,
                                position: "right" as const,
                                grid: {
                                    drawOnChartArea: false,
                                },
                                min: 0,
                                max: 5,
                            }
                        }
                    }}
                />
            </Grid>
        </>
    )
}
